import type { FC } from "react";
import { Banner } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";

/**
 * Where access that notifications can't reach comes from:
 *
 * - `"microsoftGroup"` — a Microsoft (Entra) group, carried in the sign-in token.
 * - `"support"` — the support role, which makes the user a tenant admin.
 */
export type NotificationAccessBannerReason = "microsoftGroup" | "support";

export interface NotificationAccessBannerProps {
  readonly reason: NotificationAccessBannerReason;
}

const GRANT_ACCESS =
  "To receive notifications, you will need to grant app access to yourself through the Users & Permissions settings.";

/**
 * Why this user may not be notified at all.
 *
 * Access from either source is granted per request and never stored as a
 * grant. Recipient resolution only reads stored grants, so these users are
 * dropped at send time until they grant themselves access in Users &
 * Permissions.
 *
 * Support users are also told that impersonating doesn't change whose
 * preferences this is: the page lists the impersonated user's cameras, but
 * reads and saves the support user's own preferences.
 */
export const NotificationAccessBanner: FC<NotificationAccessBannerProps> = ({ reason }) =>
  reason === "support" ?
    <Banner
      variant="warning"
      title="We detected that you have access to this organisation through support access."
      message={`${GRANT_ACCESS} If you are impersonating a user, this page shows your own notification preferences, not theirs.`}
    />
  : <Banner
      variant="warning"
      title="We detected that you may have access to this organisation through Microsoft."
      message={GRANT_ACCESS}
    />;
